// Single source of truth for the "last message" preview text that
// gets persisted on every conversation row. Used by both the
// outgoing send path (`messagePersistence.persistSentMessage`), the
// incoming-envelope hook (`useIncomingMessages`), and the sync
// engine's batched drain (`syncEngine.drainQueue`).
//
// Backwards-compatible thin wrapper over the AppEvent registry's
// `formatMessagePreview` so callers passing a `Message.body` string
// (legacy plain text OR the JSON envelope wrapper used by voice /
// image / doc / event kinds) get a correctly-formatted chat-list
// preview without each call site needing its own parse step.
//
// Per `docs/architecture/types-and-schemas.md` §2 the preview shape
// is governed by the AppEvent body type — see
// `packages/ui/src/appEvent/formatPreview.ts` for the full kind →
// preview matrix.

import { formatMessagePreview, PREVIEW_MAX_CHARS } from '../appEvent';

export { PREVIEW_MAX_CHARS };

/**
 * Returns the truncated, whitespace-collapsed preview for a message
 * body. Returns `undefined` if the body collapses to an empty string
 * so callers can decide whether to write the field at all — Dexie
 * ignores `undefined` patch keys.
 *
 * Accepts either a `Message.body` string (legacy plain text OR a
 * `{kind, payload}` JSON wrapper) or raw plaintext `Uint8Array`
 * straight off the SDK. The registry's `parseEnvelope` handles
 * both shapes.
 */
export function lastMessagePreview(
  body: string | Uint8Array | undefined,
): string | undefined {
  return formatMessagePreview(body);
}
